import { COMPLETED_THREAD_STATES } from "@productlane/lib/src/notifications/grouping"

const COMPLETED_STATES: ReadonlySet<string> = new Set(COMPLETED_THREAD_STATES)

/** Keys from anchor to target inclusive, in list order; just the target when the anchor left the list. */
export function rangeKeys(
	keys: readonly string[],
	anchor: string | null,
	target: string,
): string[] {
	const end = keys.indexOf(target)
	if (end === -1) return []
	const start = anchor ? keys.indexOf(anchor) : -1
	if (start === -1) return [target]
	const [from, to] = start <= end ? [start, end] : [end, start]
	return keys.slice(from, to + 1)
}

/** Shift+arrow: checks the cursor and the row it moves to, like a text selection. */
export function extendSelection(
	keys: readonly string[],
	checked: ReadonlySet<string>,
	cursor: string | undefined,
	direction: 1 | -1,
): { checked: Set<string>; cursor: string | null } {
	const index = cursor ? keys.indexOf(cursor) : -1
	const nextKey =
		index === -1 ? (keys[0] ?? null) : (keys[index + direction] ?? null)
	const next = new Set(checked)
	if (cursor && index !== -1) next.add(cursor)
	if (nextKey) next.add(nextKey)
	return { checked: next, cursor: nextKey ?? cursor ?? null }
}

/** What E and U act on: the checked rows, or the open one when nothing is checked. */
export function actionTargets(
	checked: ReadonlySet<string>,
	cursor: string | undefined,
): string[] {
	if (checked.size > 0) return [...checked]
	return cursor ? [cursor] : []
}

/** Where the cursor lands once rows are removed: the next survivor below, else above. */
export function cursorAfterRemoval(
	keys: readonly string[],
	removed: ReadonlySet<string>,
	cursor: string | undefined,
): string | null {
	const index = cursor ? keys.indexOf(cursor) : -1
	if (index === -1) return null
	for (let i = index + 1; i < keys.length; i++) {
		if (!removed.has(keys[i]!)) return keys[i]!
	}
	for (let i = index - 1; i >= 0; i--) {
		if (!removed.has(keys[i]!)) return keys[i]!
	}
	return null
}

export interface SelectionTarget {
	ids: string[]
	unreadIds: string[]
}

/** One selected group's rows: the loaded ones with live read state, plus any Cmd+A fetched past the page edge. */
export function selectionTarget(
	loaded: { rows: readonly { id: string }[]; unreadIds: string[] } | undefined,
	fetched: SelectionTarget | undefined,
): SelectionTarget | null {
	if (!loaded && !fetched) return null
	const seen = new Set(loaded?.rows.map((r) => r.id))
	const unseen = (ids: string[] | undefined) =>
		(ids ?? []).filter((id) => !seen.has(id))
	return {
		ids: [...seen, ...unseen(fetched?.ids)],
		unreadIds: [...(loaded?.unreadIds ?? []), ...unseen(fetched?.unreadIds)],
	}
}

export interface DoneCandidate {
	key: string
	ids: string[]
	thread: { id: string; state: string } | null
}

/**
 * E on the Notifications page: archives every target group and closes only the open group's
 * thread, so a bulk E never closes threads nobody looked at. A thread already in Done or
 * Close loop stays as it is, so E never reopens or skips a close loop.
 */
export function donePlan(
	candidates: readonly DoneCandidate[],
	openKey: string | undefined,
): {
	threadIds: string[]
	ids: string[]
} {
	const open = openKey
		? candidates.find((c) => c.key === openKey)?.thread
		: undefined
	return {
		threadIds: open && !COMPLETED_STATES.has(open.state) ? [open.id] : [],
		ids: candidates.flatMap((c) => c.ids),
	}
}
